// src/permissions/Permissions.js

/**
 * View permissions helpers (pure functions, no React).
 *
 * write / read field values:
 *   "SYSTEM"     => platform built-in — always immutable
 *   "NONE"       => explicitly locked — nobody can edit
 *   "OWNER"      => view owner only
 *   "ADMIN"      => all admins
 *   "SUPERADMIN" => super admins only
 *   string[]     => explicit userId list e.g. ["uuid1", "uuid2"]
 */

// ── Internal helpers ──────────────────────────────────────────────────────────

const SENTINELS = new Set(["SYSTEM", "NONE", "OWNER", "ADMIN", "SUPERADMIN"]);

function toUserIdSet(field) {
  if (!field) return null;
  if (Array.isArray(field)) {
    return field.length > 0 ? new Set(field) : null;
  }
  return null;
}

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Can this user write (edit/delete) the given view?
 *
 * @param {string | string[]} write       - the view's write field
 * @param {string}            owner       - the view's owner field (userId who created it)
 * @param {string}            userId      - current user's id
 * @param {boolean}           isSuperAdmin
 */
export function canWriteView({ write, owner, userId, isSuperAdmin }) {
  if (!write) return false;

  if (write === "SYSTEM")     return false;       // platform built-in — always immutable
  if (write === "NONE")       return false;       // explicitly locked by owner/admin
  if (write === "ADMIN")      return true;
  if (write === "SUPERADMIN") return !!isSuperAdmin;
  if (write === "OWNER")      return !!userId && userId === owner;

  // Array (preferred) or legacy comma-separated string — explicit userId list
  const set = toUserIdSet(write);
  return set ? set.has(userId) : false;
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Was this view specifically shared with this user?
 * Returns true only when read is an explicit userId list containing them.
 * read: "ADMIN" / "SUPERADMIN" is normal role-based access, not a personal share.
 *
 * @param {string | string[]} read   - the view's read field
 * @param {string}            userId - current user's id
 */
export function isSharedWithUser({ read, userId }) {
  if (!read) return false;
  if (read === "ADMIN" || read === "SUPERADMIN" || read === "NONE" || read === "SYSTEM") return false;

  // Array (preferred) or legacy comma-separated string — explicit userId list
  const set = toUserIdSet(read);
  return set ? set.has(userId) : false;
}

// ── Decoration ────────────────────────────────────────────────────────────────

/**
 * Stamps permission-derived flags onto a flat list of raw view objects.
 * Kept for compatibility with any existing callers.
 */
export function decorateViewsWithPermissions(flatViews, { userId, isSuperAdmin }) {
  return (flatViews || []).map((v) => {
    const isSystem = v?.write === "SYSTEM";

    const writable = canWriteView({
      write: v?.write,
      owner: v?.owner,
      userId,
      isSuperAdmin,
    });

    return {
      ...v,
      _writable: writable,
      _isSystem: isSystem,
      _locked: !writable,
    };
  });
}

// ── Convenience predicates ────────────────────────────────────────────────────

export function isViewLocked(v) {
  return !!v?._locked || !!v?._isSystem;
}

export function isViewEditable(v) {
  return !isViewLocked(v);
}

/**
 * Should edits to this view be persisted?
 * Never persists default views regardless of write permission.
 */
export function shouldPersistViewEdits(view, { userId, isSuperAdmin }) {
  if (!view) return false;

  // canWriteView handles SYSTEM, NONE, OWNER, ADMIN, SUPERADMIN, and userId lists.
  // No separate defaultView check — a default view with write=ADMIN is persisted normally.
  return canWriteView({
    write:       view.write,
    owner:       view.owner,
    userId,
    isSuperAdmin,
  });
}